import { useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const SelloutDashboard = ({ orders }) => {
    // Processar dados para os graficos
    const dashboardData = useMemo(() => {
        if (!orders || orders.length === 0) {
            return null;
        }

        // 1. Receita diaria ao longo do tempo
        const dailyRevenue = {};
        const statusDistribution = {};
        const paymentMethods = {};
        let totalRevenue = 0;
        let totalShipping = 0;
        let totalTaxes = 0;
        let totalFees = 0;

        orders.forEach((order) => {
            const date = new Date(order.date_created).toISOString().split('T')[0];
            const revenue = Number.parseFloat(order.total_amount) || 0;

            // Receita diaria
            dailyRevenue[date] = (dailyRevenue[date] || 0) + revenue;

            // Status
            statusDistribution[order.status] = (statusDistribution[order.status] || 0) + 1;

            // Metodos de pagamento
            const paymentType = order.payment_type || 'Nao informado';
            paymentMethods[paymentType] = (paymentMethods[paymentType] || 0) + revenue;

            // Totais
            totalRevenue += revenue;
            totalShipping += Number.parseFloat(order.shipping_cost) || 0;
            totalTaxes += Number.parseFloat(order.taxes_amount) || 0;

            // Calcular fees dos items
            if (order.order_items) {
                order.order_items.forEach((item) => {
                    totalFees += Number.parseFloat(item.sale_fee) || 0;
                });
            }
        });

        // 2. Top 10 produtos mais vendidos
        const productSales = {};
        orders.forEach((order) => {
            if (order.order_items) {
                order.order_items.forEach((item) => {
                    const key = item.item_id;
                    if (!productSales[key]) {
                        productSales[key] = {
                            title: item.item_title || 'Sem titulo',
                            quantity: 0,
                            revenue: 0,
                        };
                    }
                    productSales[key].quantity += Number.parseInt(item.quantity, 10) || 0;
                    productSales[key].revenue +=
                        (Number.parseFloat(item.unit_price) || 0) * (Number.parseInt(item.quantity, 10) || 0);
                });
            }
        });

        // 3. Analise de custos (Receita vs Custos)
        const costBreakdown = [
            { name: 'Receita Liquida', value: totalRevenue - totalShipping - totalTaxes - totalFees },
            { name: 'Frete', value: totalShipping },
            { name: 'Impostos', value: totalTaxes },
            { name: 'Taxas ML', value: totalFees },
        ];

        // 4. Pedidos por status
        const statusData = Object.entries(statusDistribution).map(([status, count]) => ({
            status: status,
            count: count,
        }));

        // 5. Receita por metodo de pagamento
        const paymentData = Object.entries(paymentMethods)
            .map(([method, revenue]) => ({
                method: method,
                revenue: revenue,
            }))
            .sort((a, b) => b.revenue - a.revenue);

        // 6. Evolucao de receita diaria
        const revenueTimeline = Object.entries(dailyRevenue)
            .map(([date, revenue]) => ({
                date: new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
                revenue: revenue,
            }))
            .sort((a, b) => new Date(a.date) - new Date(b.date));

        // Top 10 produtos
        const topProducts = Object.entries(productSales)
            .map(([_id, data]) => ({
                name: data.title.length > 20 ? `${data.title.substring(0, 20)}...` : data.title,
                fullName: data.title,
                quantity: data.quantity,
                revenue: data.revenue,
            }))
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 10);

        return {
            revenueTimeline,
            topProducts,
            costBreakdown,
            statusData,
            paymentData,
            totalRevenue,
            totalOrders: orders.length,
        };
    }, [orders]);

    if (!dashboardData) {
        return (
            <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-12 text-center">
                <p className="text-mono-600 dark:text-mono-400">Nenhum dado disponivel para exibir graficos</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header com resumo */}
            <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                <h2 className="text-xl font-bold text-black dark:text-white mb-4">Dashboard de Analise</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <p className="text-sm text-mono-600 dark:text-mono-400">Receita Total</p>
                        <p className="text-2xl font-bold text-black dark:text-white">
                            R$ {dashboardData.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-mono-600 dark:text-mono-400">Total de Pedidos</p>
                        <p className="text-2xl font-bold text-black dark:text-white">{dashboardData.totalOrders}</p>
                    </div>
                    <div>
                        <p className="text-sm text-mono-600 dark:text-mono-400">Ticket Medio</p>
                        <p className="text-2xl font-bold text-black dark:text-white">
                            R${' '}
                            {(dashboardData.totalRevenue / dashboardData.totalOrders).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Grid de graficos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 1. Evolucao de Receita Diaria */}
                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
                        Evolucao de Receita Diaria
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dashboardData.revenueTimeline}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
                            <YAxis
                                stroke="#9ca3af"
                                style={{ fontSize: '12px' }}
                                tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1f2937',
                                    border: '1px solid #374151',
                                    borderRadius: '8px',
                                    color: '#f3f4f6',
                                }}
                                labelStyle={{ color: '#f3f4f6' }}
                                formatter={(value) => [
                                    `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                                    'Receita',
                                ]}
                            />
                            <Line
                                type="monotone"
                                dataKey="revenue"
                                stroke="#10b981"
                                strokeWidth={2}
                                dot={{ fill: '#10b981', r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* 2. Top 10 Produtos por Receita */}
                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
                        Top 10 Produtos por Receita
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dashboardData.topProducts} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis
                                type="number"
                                stroke="#9ca3af"
                                style={{ fontSize: '12px' }}
                                tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                stroke="#9ca3af"
                                style={{ fontSize: '10px' }}
                                width={120}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1f2937',
                                    border: '1px solid #374151',
                                    borderRadius: '8px',
                                }}
                                formatter={(value, name, _props) => {
                                    if (name === 'revenue') {
                                        return [
                                            `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                                            'Receita',
                                        ];
                                    }
                                    return [value, 'Quantidade'];
                                }}
                                labelFormatter={(label, payload) => {
                                    if (payload && payload[0] && payload[0].payload.fullName) {
                                        return payload[0].payload.fullName;
                                    }
                                    return label;
                                }}
                            />
                            <Bar dataKey="revenue" fill="#3b82f6" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* 3. Distribuicao de Custos */}
                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-black dark:text-white mb-4">Distribuicao de Custos</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={dashboardData.costBreakdown}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {dashboardData.costBreakdown.map((_entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1f2937',
                                    border: '1px solid #374151',
                                    borderRadius: '8px',
                                }}
                                formatter={(value) =>
                                    `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                }
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* 4. Pedidos por Status */}
                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-black dark:text-white mb-4">Pedidos por Status</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dashboardData.statusData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="status" stroke="#9ca3af" style={{ fontSize: '12px' }} />
                            <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1f2937',
                                    border: '1px solid #374151',
                                    borderRadius: '8px',
                                }}
                                formatter={(value) => [value, 'Pedidos']}
                            />
                            <Bar dataKey="count" fill="#8b5cf6" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* 5. Receita por Metodo de Pagamento */}
                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
                        Receita por Metodo de Pagamento
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={dashboardData.paymentData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ method, percent }) => `${method}: ${(percent * 100).toFixed(1)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="revenue"
                            >
                                {dashboardData.paymentData.map((_entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1f2937',
                                    border: '1px solid #374151',
                                    borderRadius: '8px',
                                }}
                                formatter={(value) =>
                                    `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                }
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* 6. Top 10 Produtos por Quantidade */}
                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-black dark:text-white mb-4">
                        Top 10 Produtos por Quantidade Vendida
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dashboardData.topProducts}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis
                                dataKey="name"
                                stroke="#9ca3af"
                                style={{ fontSize: '9px' }}
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                interval={0}
                            />
                            <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1f2937',
                                    border: '1px solid #374151',
                                    borderRadius: '8px',
                                }}
                                formatter={(value, name) => {
                                    if (name === 'quantity') {
                                        return [value, 'Unidades'];
                                    }
                                    return [
                                        `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                                        'Receita',
                                    ];
                                }}
                                labelFormatter={(label, payload) => {
                                    if (payload && payload[0] && payload[0].payload.fullName) {
                                        return payload[0].payload.fullName;
                                    }
                                    return label;
                                }}
                            />
                            <Bar dataKey="quantity" fill="#f59e0b" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default SelloutDashboard;
